import React, { useState, useEffect } from 'react';
import '../styles/ScheduleMemoModal.css';

const ScheduleMemoModal = ({ 
  isOpen, 
  onClose, 
  scheduleInfo, 
  onSaveMemo, 
  onUpdateMemo, 
  onDeleteMemo, 
  showWarning,
  existingMemo = null 
}) => {
  const [memo, setMemo] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);

  // 매칭대기 메모인지 확인
  const isPendingMemo = scheduleInfo?.status === 'pending' || scheduleInfo?.status === 'pending_with_memo';

  // 기존 메모가 있으면 편집 모드로 설정
  useEffect(() => {
    if (existingMemo) {
      setMemo(existingMemo.memo || existingMemo.reason || '');
      setSelectedDates(existingMemo.unavailableDates || [existingMemo.unavailableDate].filter(Boolean) || []);
      setIsEditing(true);
    } else {
      setMemo('');
      setSelectedDates([]);
      setIsEditing(false);
    }
  }, [existingMemo, isOpen]);

  // 해당 요일의 2달치 날짜 옵션 생성
  const generateDateOptions = () => {
    const options = [];
    const today = new Date();
    
    // 요일 매핑
    const dayOfWeekMap = {
      'monday': 1,    // 월요일
      'tuesday': 2,   // 화요일
      'wednesday': 3, // 수요일
      'thursday': 4,  // 목요일
      'friday': 5,    // 금요일
      'saturday': 6,  // 토요일
      'sunday': 0     // 일요일
    };
    
    // 해당 요일의 인덱스
    const targetDayIndex = dayOfWeekMap[scheduleInfo?.dayOfWeek];
    
    if (targetDayIndex !== undefined) {
      // 해당 요일의 날짜만 찾기 (2달치, 당일 제외)
      for (let i = 1; i < 61; i++) { // 2달 (약 8주), 내일부터 시작
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        
        // 해당 요일인지 확인
        if (date.getDay() === targetDayIndex) {
          const dateString = date.toISOString().split('T')[0];
          const formattedDate = date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long'
          });
          
          options.push({
            value: dateString,
            label: formattedDate
          });
        }
      }
    }
    
    return options;
  };

  // 날짜 선택/해제 핸들러
  const handleDateToggle = (dateValue) => {
    setSelectedDates(prev => {
      if (prev.includes(dateValue)) {
        // 이미 선택된 날짜면 제거
        return prev.filter(date => date !== dateValue);
      } else {
        // 새로운 날짜면 추가
        return [...prev, dateValue];
      }
    });
  };

  const handleSave = () => {
    if (!memo.trim()) {
      showWarning('변경 사유를 입력해주세요.');
      return;
    }

    if (isPendingMemo && selectedDates.length === 0) {
      showWarning('불가능한 날짜를 선택해주세요.');
      return;
    }

    if (isEditing && existingMemo) {
      // 기존 메모 수정
      const updateData = {
        memo: memo.trim(),
        reason: memo.trim(),
        unavailableDates: selectedDates
      };
      onUpdateMemo(existingMemo.id, updateData);
    } else {
      // 새 메모 저장
      const memoData = {
        scheduleId: scheduleInfo.id,
        time: scheduleInfo.time,
        student: scheduleInfo.studentName || (isPendingMemo ? '매칭 대기' : '학생 정보 없음'),
        date: scheduleInfo.date,
        dayOfWeek: scheduleInfo.dayOfWeek,
        memo: memo.trim(),
        reason: memo.trim(),
        timestamp: new Date().toISOString(),
        tutorName: scheduleInfo.tutorName || '현재 튜터',
        unavailableDates: selectedDates
      };
      onSaveMemo(memoData);
    }
    
    setMemo('');
    setSelectedDates([]);
    onClose();
  };

  const handleDelete = () => {
    if (existingMemo) {
      onDeleteMemo(existingMemo.id, scheduleInfo.id);
      setMemo('');
      setSelectedDates([]);
      onClose();
    }
  };

  const handleClose = () => {
    setMemo('');
    setSelectedDates([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="memo-modal-overlay" onClick={handleClose}>
      <div className="memo-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="memo-modal-header">
          <h3>
            {isEditing ? '✏️ 스케줄 메모 수정' : '📝 스케줄 메모 작성'}
            {isPendingMemo && <span className="pending-badge">매칭대기</span>}
          </h3>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="memo-modal-body">
          <div className="schedule-info">
            <h4>📅 스케줄 정보</h4>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">시간:</span>
                <span className="value">{scheduleInfo?.time}</span>
              </div>
              <div className="info-item">
                <span className="label">요일:</span>
                <span className="value">{scheduleInfo?.dayOfWeek ? 
                  scheduleInfo.dayOfWeek.replace('monday', '월요일')
                    .replace('tuesday', '화요일')
                    .replace('wednesday', '수요일')
                    .replace('thursday', '목요일')
                    .replace('friday', '금요일')
                    .replace('saturday', '토요일')
                    .replace('sunday', '일요일') : 
                  scheduleInfo?.date
                }</span>
              </div>
                              {!isPendingMemo && (
                  <div className="info-item">
                    <span className="label">학생:</span>
                    <span className="value">{scheduleInfo?.studentName || '학생 정보 없음'}</span>
                  </div>
                )}
                <div className="info-item">
                  <span className="label">상태:</span>
                  <span className="value">{
                    scheduleInfo?.status === 'pending' ? '매칭 대기' :
                    scheduleInfo?.status === 'pending_with_memo' ? '매칭 대기 메모 등록' :
                    scheduleInfo?.status === 'confirmed' ? '확정완료' :
                    scheduleInfo?.status === 'registered' ? '메모등록' : '알 수 없음'
                  }</span>
                </div>
                {scheduleInfo?.lessonType && (
                  <div className="info-item">
                    <span className="label">수업종류:</span>
                    <span className="value">{scheduleInfo.lessonType === 'trial' ? '체험수업' : '정규수업'}</span>
                  </div>
                )}
                {scheduleInfo?.lessonDate && scheduleInfo?.lessonType === 'trial' && (
                  <div className="info-item">
                    <span className="label">수업날짜:</span>
                    <span className="value">{new Date(scheduleInfo.lessonDate).toLocaleDateString('ko-KR')}</span>
                  </div>
                )}
            </div>
          </div>

          {/* 매칭대기 메모일 때만 날짜 선택 필드 표시 */}
          {isPendingMemo && (
            <div className="date-selection-section">
              <label htmlFor="unavailable-date">🚫 불가능한 날짜 선택 *</label>
              <div className="date-checkbox-list">
                {generateDateOptions().map(option => (
                  <div 
                    key={option.value} 
                    className={`date-checkbox-item ${selectedDates.includes(option.value) ? 'checked' : ''}`}
                    onClick={() => handleDateToggle(option.value)}
                  >
                    <span>{option.label}</span>
                  </div>
                ))}
              </div>
              <div className="date-help-text">
                💡 {scheduleInfo?.dayOfWeek ? 
                  `${scheduleInfo.dayOfWeek.replace('monday', '월요일')
                    .replace('tuesday', '화요일')
                    .replace('wednesday', '수요일')
                    .replace('thursday', '목요일')
                    .replace('friday', '금요일')
                    .replace('saturday', '토요일')
                    .replace('sunday', '일요일')} 날짜만 표시됩니다.` : 
                  '선택한 날짜는 해당 요일의 시간대에서 배정이 불가능합니다.'
                }
              </div>
              {selectedDates.length > 0 && (
                <div className="selected-dates-summary">
                  <strong>선택된 날짜 ({selectedDates.length}개):</strong>
                  <div className="selected-dates-list">
                    {selectedDates
                      .slice() // 원본 배열을 변경하지 않기 위해 복사
                      .sort((a, b) => new Date(a) - new Date(b)) // 오름차순 정렬
                      .map(dateValue => {
                      const dateObj = new Date(dateValue);
                      const formattedDate = dateObj.toLocaleDateString('ko-KR', {
                        month: 'short',
                        day: 'numeric'
                      });
                      return (
                        <span key={dateValue} className="selected-date-tag">
                          {formattedDate}
                          <button 
                            type="button" 
                            onClick={() => handleDateToggle(dateValue)}
                            className="remove-date-btn"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="memo-section">
            <label htmlFor="memo">
              {isPendingMemo ? '✏️ 불가능 사유 *' : '✏️ 변경 사유 *'}
            </label>
            <textarea
              id="memo"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder={
                isPendingMemo 
                  ? "해당 날짜에 수업이 불가능한 사유를 입력해주세요.\n\n예시:\n- 개인 일정 (병원, 약속 등)\n- 다른 수업과의 시간 겹침\n- 급한 개인 사정 발생 등"
                  : "스케줄 변경 사유를 입력해주세요.\n\n예시:\n- 학생의 갑작스러운 일정 변경 요청\n- 다른 수업과의 시간 겹침\n- 개인 급한 일정 발생 등"
              }
              className="memo-textarea"
              rows="6"
            />
            <div className="char-count">
              {memo.length}/500자
            </div>
          </div>
        </div>

        <div className="memo-modal-footer">
          <button className="cancel-btn" onClick={handleClose}>
            취소
          </button>
          {isEditing && (
            <button className="delete-btn" onClick={handleDelete}>
              🗑️ 메모 삭제
            </button>
          )}
          <button className="save-btn" onClick={handleSave}>
            {isEditing ? '💾 메모 수정' : '💾 메모 저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleMemoModal; 